<template>
  <div class="overflow-x-auto -mx-4 sm:mx-0">
    <div class="inline-block min-w-full align-middle px-4 sm:px-0">
      <div class="overflow-hidden border border-gray-200 dark:border-gray-800 rounded-lg">
        <table class="min-w-full">
          <tfoot>
            <tr>
              <td
                colspan="2"
                class="sm:colspan-3 px-2 sm:px-4 py-1 text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 text-right"
              >
                {{ $t('app.views.orders.modals.details.subtotal') }}
              </td>
              <td class="hidden sm:table-cell" />
              <td class="px-2 sm:px-4 py-1 text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 text-right">
                ${{ (order.subtotal || 0).toFixed(2) }}
              </td>
            </tr>

            <!-- Promotions Applied Details -->
            <template v-if="hasPromotions">
              <!-- Applied Promotions List -->
              <tr v-if="validPromotions && validPromotions.length > 0">
                <td
                  colspan="4"
                  class="px-2 sm:px-4 py-2 bg-green-50 dark:bg-green-900/20"
                >
                  <div class="space-y-1">
                    <div
                      v-for="promotion in validPromotions"
                      :key="promotion.promotion_id || promotion.id || Math.random()"
                      class="flex justify-between items-center text-sm"
                    >
                      <span class="font-medium text-green-700 dark:text-green-300">
                        🎉 {{ promotion.promotion_name || promotion.name || 'Promoción' }}
                      </span>
                      <span class="font-bold text-green-800 dark:text-green-200">
                        -${{ (promotion.total_discount || promotion.discount_amount || 0).toFixed(2) }}
                      </span>
                    </div>
                    <div
                      v-if="promotionMessages && promotionMessages.length > 0"
                      class="mt-1"
                    >
                      <div
                        v-for="message in promotionMessages"
                        :key="message"
                        class="text-xs text-green-600 dark:text-green-400 italic"
                      >
                        {{ message }}
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
              <!-- Debug: Show raw data -->
              <tr
                v-if="order.applied_promotions && order.applied_promotions.length > 0 && (!validPromotions || validPromotions.length === 0)"
              >
                <td
                  colspan="4"
                  class="px-2 sm:px-4 py-2 bg-red-50 dark:bg-red-900/20"
                >
                  <div class="text-xs text-red-600 dark:text-red-400">
                    DEBUG: applied_promotions exists but validPromotions is empty
                    <br>Raw: {{ JSON.stringify(order.applied_promotions) }}
                    <br>Valid: {{ JSON.stringify(validPromotions) }}
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="(order.tax || 0) > 0">
              <td
                colspan="2"
                class="sm:colspan-3 px-2 sm:px-4 py-1 text-sm sm:text-base text-gray-600 dark:text-gray-300 text-right font-medium"
              >
                {{ $t('app.views.orders.modals.details.tax', {
                  rate: ((order.taxRate || 0) * 100).toFixed(1)
                }) }}
              </td>
              <td class="hidden sm:table-cell" />
              <td
                class="px-2 sm:px-4 py-1 text-sm sm:text-base text-gray-600 dark:text-gray-300 text-right font-medium"
              >
                ${{ (order.tax || 0).toFixed(2) }}
              </td>
            </tr>

            <!-- Debug: Always show delivery fee for delivery orders -->
            <tr v-if="order.order_type === 'Delivery' || order.order_type === 'delivery'">
              <td
                colspan="2"
                class="sm:colspan-3 px-2 sm:px-4 py-1 text-sm sm:text-base text-orange-600 dark:text-orange-400 text-right font-medium"
              >
                Costo de Envío
              </td>
              <td class="hidden sm:table-cell" />
              <td
                class="px-2 sm:px-4 py-1 text-sm sm:text-base text-orange-600 dark:text-orange-400 text-right font-medium"
              >
                +${{ (order.delivery_fee || 0).toFixed(2) }}
              </td>
            </tr>
            <tr v-if="ivaEnabled && ivaAmount > 0">
              <td
                colspan="2"
                class="sm:colspan-3 px-2 sm:px-4 py-1 text-sm sm:text-base text-blue-600 dark:text-blue-400 text-right font-medium"
              >
                IVA ({{ (ivaRate * 100).toFixed(0) }}%)
              </td>
              <td class="hidden sm:table-cell" />
              <td
                class="px-2 sm:px-4 py-1 text-sm sm:text-base text-blue-600 dark:text-blue-400 text-right font-medium"
              >
                +${{ ivaAmount.toFixed(2) }}
              </td>
            </tr>
            <tr>
              <td
                colspan="2"
                class="sm:colspan-3 px-2 sm:px-4 py-2 sm:py-3 text-base sm:text-lg font-bold text-gray-900 dark:text-white text-right"
              >
                {{ $t('app.views.orders.modals.details.total') }}
              </td>
              <td class="hidden sm:table-cell" />
              <td
                class="px-2 sm:px-4 py-2 sm:py-3 text-base sm:text-lg font-bold text-gray-900 dark:text-white text-right"
              >
                ${{ totalWithIVA.toFixed(2) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { calculateIvaBreakdown } from '@/utils/ivaHelper'

interface Props {
  order: {
    subtotal?: number
    tax?: number
    taxRate?: number
    delivery_fee?: number
    order_type?: string
    applied_promotions?: any[]
    iva_enabled?: boolean
    promotion_discount?: number
    discount_amount?: number
  }
  ivaEnabled?: boolean
  promotionMessages?: string[]
}

const props = defineProps<Props>()
const { t } = useI18n()
const authStore = useAuthStore()

const ivaRate = computed(() => authStore.restaurant?.iva_rate || 0.16)

const ivaCalculation = computed(() => {
  return calculateIvaBreakdown({
    subtotal: props.order.subtotal || 0,
    discount: props.order.promotion_discount || 0,
    deliveryFee: props.order.delivery_fee || 0,
    ivaRate: ivaRate.value,
    enabled: props.ivaEnabled || false
  })
})

const ivaAmount = computed(() => ivaCalculation.value.ivaAmount)

const totalWithIVA = computed(() => {
  const tax = props.order.tax || 0
  const discount = totalPromotionDiscount.value
  const subtotal = props.order.subtotal || 0
  const deliveryFee = props.order.delivery_fee || 0

  // Calculate IVA on the amount after discount
  const ivaApplicableAmount = subtotal - discount + deliveryFee
  const ivaAmount = props.ivaEnabled ? ivaApplicableAmount * ivaRate.value : 0

  return subtotal - discount + deliveryFee + ivaAmount + tax
})

const totalSavings = computed(() => {
  return totalPromotionDiscount.value
})

const hasPromotions = computed(() => {
  return (Array.isArray(props.order.applied_promotions) && props.order.applied_promotions.length > 0) ||
    (props.order.promotion_discount && props.order.promotion_discount > 0);
})

// Calculate total promotion discount
const totalPromotionDiscount = computed(() => {
  // Try to get from applied_promotions first
  if (Array.isArray(props.order.applied_promotions) && props.order.applied_promotions.length > 0) {
    return props.order.applied_promotions.reduce((total, promotion) => {
      return total + (promotion.total_discount || promotion.discount_amount || 0);
    }, 0);
  }

  // Fallback to promotion_discount field
  return props.order.promotion_discount || props.order.discount_amount || 0;
})

// Get valid promotions (filter out invalid ones to prevent infinite loop)
const validPromotions = computed(() => {
  let promotionsArray = props.order.applied_promotions;

  // Handle case where applied_promotions is a JSON string
  if (typeof promotionsArray === 'string') {
    try {
      promotionsArray = JSON.parse(promotionsArray);
    } catch (error) {
      console.error('Failed to parse applied_promotions JSON:', error);
      return [];
    }
  }

  if (!promotionsArray || !Array.isArray(promotionsArray)) {
    return [];
  }

  // Filter promotions that have a valid discount amount (> 0) or valid ID
  const filtered = promotionsArray.filter(promotion => {
    const discount = promotion.total_discount || promotion.discount_amount || 0;
    const hasValidId = promotion.promotion_id || promotion.id;
    const hasValidName = promotion.promotion_name || promotion.name;

    // Only include promotions with valid discount, ID, or name
    return discount > 0 || hasValidId || hasValidName;
  });

  const result = filtered.slice(0, 10); // Limit to max 10 promotions to prevent UI issues

  return result;
})
</script>
